import {
  AccountProductEnum,
  IDisplayBalance,
  IInternalAccount,
  InternalAccountTile,
  TileStatus,
} from '@varo/graphql-schema-types';
import { mapInternalAccountChildTiles } from 'features/home/HomeScreen/adapters/internalAccountTile/accountActions/mapInternalAccountChildTiles';
import { HomeCardDependencies } from 'features/home/HomeScreen/dependencies/types';
import { AccountIconType } from 'shared/components/vxl/AccountIcon/AccountIcon.helpers';
import { HorizontalAccountCardProps } from 'shared/components/vxl/cards/HorizontalAccountCard/HorizontalAccountCard';
import { TagProps } from 'shared/components/vxl/Tag/Tag';
import { propsFromTag } from 'shared/components/vxl/Tag/Tag.helpers';
import { VXLComponent, VXLComponentId } from 'shared/renderer/types';
import { Maybe } from 'shared/utils/types';

const titleAccessory = (account: IInternalAccount): Maybe<TagProps> => {
  const { tag } = account;
  return propsFromTag(tag);
};

interface InternalAccountTileNode extends VXLComponent {
  id: string;
  name: VXLComponentId.HORIZONTAL_ACCOUNT_CARD;
  props: HorizontalAccountCardProps;
}

const internalAccountTile = (
  tile: InternalAccountTile,
  dependencies: HomeCardDependencies,
): Maybe<InternalAccountTileNode> => {
  if (tile.status !== TileStatus.Loaded || !tile.account) {
    return null;
  }
  const { account } = tile;
  const { nickname } = account;
  const { displayBalance } = account as IDisplayBalance;
  const { goToAccount } = dependencies;
  const accountActions = mapInternalAccountChildTiles(tile.tiles, dependencies);

  const getProduct = (product: AccountProductEnum): AccountIconType => {
    switch (product) {
      case AccountProductEnum.Advance:
        return AccountIconType.Advance;
      case AccountProductEnum.ChargeCard:
        return AccountIconType.ChargeCard;
      case AccountProductEnum.Checking:
        return AccountIconType.Checking;
      case AccountProductEnum.LineOfCredit:
        return AccountIconType.LineOfCredit;
      case AccountProductEnum.Savings:
        return AccountIconType.Savings;
      default:
        return AccountIconType.Unknown;
    }
  };

  function getAnalyticsId(product: AccountProductEnum) {
    switch (product) {
      case AccountProductEnum.Savings:
        return 'savingsAccountCardClicked';
      case AccountProductEnum.ChargeCard:
        return 'chargeCardAccountClicked';
      case AccountProductEnum.Checking:
        return 'checkingAccountCardClicked';
    }
  }

  return {
    id: tile.id,
    name: VXLComponentId.HORIZONTAL_ACCOUNT_CARD,
    props: {
      accountIconProps: {
        product: getProduct(account.product),
      },
      titleText: nickname,
      titleAccessory: titleAccessory(account),
      amountText: displayBalance?.amount.formatted ?? '',
      onAddIconClick: undefined,
      isAmountStale: false,
      accountActions: accountActions ?? undefined,
      disabled: false,
      onCardClick: () => {
        goToAccount(account.id);
      },
      analyticsId: getAnalyticsId(account.product),
      customAttributes: {
        account_type: account.product.toLowerCase(),
      },
    },
  };
};

export default internalAccountTile;
